/**
 * Admin Transfers Screen
 * View internal WiTon transfers between users
 */

import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';

interface Transfer {
  id: string;
  sender: string;
  recipient: string;
  amount: number;
  createdAt: string;
}

export function AdminTransfers() {
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchTransfers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getAdminTransfers({ page, limit: 50 });
      setTransfers(res.data.transfers || []);
      setTotalPages(res.data.totalPages || 1);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to load transfers', err);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchTransfers(); }, [fetchTransfers]);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0a1628 0%, #000814 100%)',
      color: '#fff',
      padding: '16px',
    }}>
      <div style={{
        background: 'rgba(59, 130, 246, 0.1)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        borderRadius: '10px',
        padding: '12px',
        textAlign: 'center',
        marginBottom: '16px',
      }}>
        <div style={{ fontSize: '11px', color: '#60a5fa', marginBottom: '4px' }}>Total Transfers</div>
        <div style={{ fontSize: '22px', fontWeight: 700 }}>{total}</div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading...</div>
      ) : transfers.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>📭</div>
          <div>No transfers found</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {transfers.map(t => (
            <div key={t.id} style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '12px',
              padding: '12px 14px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div>
                <div style={{ fontSize: '13px', fontWeight: 600 }}>
                  <span style={{ color: '#ef4444' }}>{t.sender}</span>
                  <span style={{ color: '#6b7280', margin: '0 6px' }}>→</span>
                  <span style={{ color: '#10b981' }}>{t.recipient}</span>
                </div>
                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                  {new Date(t.createdAt).toLocaleString()}
                </div>
              </div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#60a5fa' }}>
                {t.amount.toLocaleString()} WiTon
              </div>
            </div>
          ))}

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '12px' }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: page <= 1 ? 'rgba(255,255,255,0.05)' : 'rgba(59,130,246,0.3)', color: page <= 1 ? '#6b7280' : '#60a5fa', fontSize: '13px', fontWeight: 600, cursor: page <= 1 ? 'not-allowed' : 'pointer' }}>
                ← Prev
              </button>
              <span style={{ fontSize: '13px', color: '#9ca3af' }}>Page {page} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: page >= totalPages ? 'rgba(255,255,255,0.05)' : 'rgba(59,130,246,0.3)', color: page >= totalPages ? '#6b7280' : '#60a5fa', fontSize: '13px', fontWeight: 600, cursor: page >= totalPages ? 'not-allowed' : 'pointer' }}>
                Next →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
